import {
  Badge,
  Paper,
  Status,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography,
  WithTooltip,
} from '@compass/components'
import {
  CategoryIcon,
  formatFraudScore,
  getFraudScoreLabels,
  sortFraudScoreLabels,
  useFraudScoreEnabled,
  useFraudScoreLabelDisplay,
} from 'features/fraudAnalysis'
import { useCurrentSubscription } from 'features/subscription'
import { InfoIcon } from 'lucide-react'
import { FraudScoreLabelPrediction, IdEventV4 } from 'models/event'
import { useMemo } from 'react'

/**
 * Per-label Fraud Score predictions of an event, shown in the Smart Signals section.
 * Renders nothing unless Fraud Score is enabled for the workspace and the event has labels.
 */
export function FraudScoreCard({ event }: { event?: IdEventV4 }) {
  const { currentSubscriptionId } = useCurrentSubscription()
  const { isEnabled } = useFraudScoreEnabled(currentSubscriptionId)
  const labels = useMemo(() => sortFraudScoreLabels(getFraudScoreLabels(event)), [event])
  const isVisible = isEnabled && labels.length > 0
  const { getLabelName } = useFraudScoreLabelDisplay(currentSubscriptionId, { enabled: isVisible })

  if (!isVisible) {
    return null
  }

  return <FraudScoreCardView labels={labels} getLabelName={getLabelName} />
}

/** Presentational Fraud Score card; expects `labels` already sorted. */
export function FraudScoreCardView({
  labels,
  getLabelName,
}: {
  labels: readonly FraudScoreLabelPrediction[]
  getLabelName: (labelSlug: string) => string
}) {
  const detectedCount = labels.filter((l) => l.prediction).length

  return (
    <Paper padded={false} className='overflow-hidden' aria-label='Fraud Score'>
      <div className='flex items-center pl-4 pr-2 py-4'>
        <WithTooltip content='Predictions from your Fraud Score models for this event. A label is detected when its ML score reaches the threshold.'>
          <span className='flex items-center gap-1 cursor-help'>
            <Typography as='span' size='base' weight='medium' decoration='underline dotted' className='text-gray-900'>
              Fraud Score
            </Typography>
            <InfoIcon size={12} className='text-gray-800' aria-hidden />
          </span>
        </WithTooltip>
      </div>

      <Table aria-label='Fraud Score predictions' size='lg' className='border-y border-gray-300'>
        <TableHeader>
          <TableColumn isRowHeader className='w-48'>
            Label
          </TableColumn>
          <TableColumn className='w-18' aria-label='Model version'>
            <span className='sr-only'>Model version</span>
          </TableColumn>
          <TableColumn>Prediction</TableColumn>
          <TableColumn align='end' className='w-32'>
            ML score
          </TableColumn>
          <TableColumn align='end' className='w-32'>
            Threshold
          </TableColumn>
        </TableHeader>
        <TableBody>
          {labels.map((label) => (
            <FraudScoreLabelRow key={label.label} label={label} name={getLabelName(label.label)} />
          ))}
        </TableBody>
      </Table>

      <div className='flex items-center pl-4 pr-2 py-3'>
        {/* TODO(DASH-3092): replace with the final one-line summary copy once design provides it. */}
        <Typography size='xs' className='text-gray-800'>
          {detectedCount} of {labels.length} {labels.length === 1 ? 'label' : 'labels'} detected for this event.
        </Typography>
      </div>
    </Paper>
  )
}

function FraudScoreLabelRow({ label, name }: { label: FraudScoreLabelPrediction; name: string }) {
  return (
    <TableRow id={label.label} data-testid='fraud-score-label-row'>
      <TableCell>
        <span className='inline-flex h-6 max-w-full items-center gap-1.5 rounded-full border border-gray-400 bg-white pl-2 pr-3 text-xs text-gray-900'>
          <CategoryIcon categorySlug={label.category} aria-hidden className='size-3 shrink-0' />
          <span className='truncate'>{name}</span>
        </span>
      </TableCell>
      <TableCell align='end'>
        {label.version ? (
          <Badge variant='subtle' tone='secondary' mono>
            {formatModelVersion(label.version)}
          </Badge>
        ) : null}
      </TableCell>
      <TableCell>
        {label.prediction ? (
          <Status tone='error' className='text-gray-1000'>
            Detected
          </Status>
        ) : (
          <Status tone='secondary'>Not detected</Status>
        )}
      </TableCell>
      <TableCell align='end' className='font-mono'>
        {formatFraudScore(label.ml_score)}
      </TableCell>
      <TableCell align='end' className='font-mono'>
        {label.threshold != null ? (
          formatFraudScore(label.threshold)
        ) : (
          <>
            <span className='text-gray-600' aria-hidden>
              —
            </span>
            <span className='sr-only'>No threshold</span>
          </>
        )}
      </TableCell>
    </TableRow>
  )
}

function formatModelVersion(version: string) {
  return version.startsWith('v') ? version : `v${version}`
}
